import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

const STATUSES = ["whatsapp_pending", "placed", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("");

  const load = () => api.get(`/merchant/orders/${id}`).then(({ data }) => { setOrder(data); setStatus(data.status); });
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try { await api.put(`/merchant/orders/${id}/status`, { status }); toast.success("Order updated"); load(); } catch (e) { toast.error(extractErr(e)); }
  };
  const decideCancel = async (decision) => {
    try {
      await api.put(`/merchant/orders/${id}/cancel-request`, { decision });
      toast.success(decision === "approved" ? "Cancellation approved" : "Cancellation disapproved");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  if (!order) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <Link to="/admin/orders" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="order-detail-back"><ArrowLeft className="w-4 h-4" /> Back to orders</Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 font-mono-nums">{order.order_number}</h1>
          <p className="text-slate-500 text-sm mt-1">{new Date(order.created_at).toLocaleString("en-IN")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} data-testid={`status-${s}`}>{s}</SelectItem>))}</SelectContent>
          </Select>
          <Button onClick={save} data-testid="order-status-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
        </div>
      </div>
      {order.cancel_request?.reason && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="order-cancel-request">
          <div className="font-semibold">Cancelled by customer</div>
          <p className="mt-1" data-testid="order-cancel-reason">{order.cancel_request.reason}</p>
          <p className="text-xs mt-1 text-amber-800">
            {order.cancel_request.status === "pending" ? "Waiting for your decision. Approving cancels the order. Disapproving keeps it as it is." : order.cancel_request.status === "approved" ? "You approved this cancellation." : "You disapproved this cancellation."}
          </p>
          {order.cancel_request.status === "pending" && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid="order-cancel-approve" onClick={() => decideCancel("approved")}>Approve</Button>
              <Button size="sm" variant="outline" data-testid="order-cancel-reject" onClick={() => decideCancel("rejected")}>Disapprove</Button>
            </div>
          )}
        </div>
      )}
      {order.payment_method === "whatsapp" && order.status === "whatsapp_pending" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex flex-wrap items-center justify-between gap-3" data-testid="whatsapp-order-banner">
          <div>WhatsApp order — confirm after the customer messages you on WhatsApp.</div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-confirm-btn" onClick={async () => { try { await api.put(`/merchant/orders/${id}/status`, { status: "confirmed" }); toast.success("WhatsApp order confirmed"); load(); } catch (e) { toast.error(extractErr(e)); } }}>Confirm WhatsApp order</Button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 md:col-span-2 border-slate-200/70">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Items</div>
          <div className="divide-y divide-slate-100">
            {order.items.map((it) => (
              <div key={it.product_id} className="py-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                  {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{it.name}</div>
                  <div className="text-xs text-slate-500 font-mono-nums">₹{it.unit_price.toLocaleString("en-IN")} × {it.qty}</div>
                  {it.is_abp && it.abp ? (
                    <div className="mt-1 text-xs text-slate-600 space-y-0.5" data-testid="order-abp">
                      <div>{it.abp.target_kg} KG{it.abp.mix_name ? ` · ${it.abp.mix_name}` : ""}</div>
                      {(it.abp.items || []).map((b) => (
                        <div key={b.product_id}>{b.name} × {b.qty_kg} KG · ₹{Number(b.line_total || 0).toLocaleString("en-IN")}</div>
                      ))}
                      {(it.abp.services || []).map((s, i) => (
                        <div key={i}>{s.name} · {s.kg} KG × ₹{s.per_kg} = ₹{Number(s.amount || 0).toLocaleString("en-IN")}</div>
                      ))}
                    </div>
                  ) : null}
                  {it.is_box && (it.box_items || []).length > 0 && (
                    <ul className="mt-1 text-xs text-slate-600 space-y-0.5" data-testid="order-box-items">
                      {it.box_items.map((b) => (
                        <li key={b.product_id}>{b.name} × {b.qty}</li>
                      ))}
                    </ul>
                  )}
                  {(it.custom_form || []).length > 0 && (
                    <ul className="mt-1 text-xs text-slate-600 space-y-0.5" data-testid="order-custom-form">
                      {it.custom_form.map((f, i) => (
                        <li key={i}>{f.label}: {f.value}{f.qty ? ` (${f.qty} pcs)` : ""}</li>
                      ))}
                    </ul>
                  )}
                  {it.custom_print ? (
                    <div className="mt-2 space-y-1" data-testid="order-custom-print">
                      {it.custom_print.preview_url ? (
                        <img src={mediaUrl(it.custom_print.preview_url)} alt="Custom print preview" className="w-28 h-28 rounded-lg object-cover border border-slate-200" />
                      ) : null}
                      {it.custom_print.text?.value ? <div className="text-xs text-slate-600">Text: {it.custom_print.text.value}</div> : null}
                      {it.custom_print.image?.url ? <div className="text-xs text-slate-600">Customer design uploaded</div> : null}
                    </div>
                  ) : null}
                </div>
                <div className="font-mono-nums font-semibold">₹{it.line_total.toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-nums">₹{order.subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="font-mono-nums">₹{order.shipping.toLocaleString("en-IN")}</span></div>
            {(order.charges || []).map((c) => (
              <div key={c.id || c.name} className="flex justify-between" data-testid="order-charge">
                <span className="text-slate-500">{c.name}{c.kind === "percent" ? ` (${c.value}%)` : ""}</span>
                <span className="font-mono-nums">₹{Number(c.amount || 0).toLocaleString("en-IN")}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold text-slate-900 pt-1"><span>Total</span><span className="font-mono-nums">₹{order.total.toLocaleString("en-IN")}</span></div>
            {order.partial_payment && (
              <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 space-y-1" data-testid="admin-partial-payment">
                <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Partial payment</div>
                {(order.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {order.tags.map((t) => <Badge key={t} className="bg-white text-emerald-800 border border-emerald-200">{t}</Badge>)}
                  </div>
                )}
                <div className="flex justify-between text-sm"><span>Advance paid</span><span className="font-mono-nums">₹{Number(order.partial_payment.advance_paid || order.partial_payment.advance_amount || 0).toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between text-sm"><span>COD balance</span><span className="font-mono-nums">₹{Number(order.partial_payment.cod_balance || 0).toLocaleString("en-IN")}</span></div>
                {order.payment_status === "partial_paid" && (
                  <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700" data-testid="collect-cod-balance" onClick={async () => { try { await api.post(`/merchant/orders/${id}/collect-cod-balance`); toast.success("COD balance marked collected"); load(); } catch (e) { toast.error(extractErr(e)); } }}>Mark COD balance collected</Button>
                )}
              </div>
            )}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 border-slate-200/70">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Customer</div>
            <div className="text-sm font-semibold text-slate-900">{order.customer_name}</div>
            <div className="text-xs text-slate-500 mt-1">{order.customer_email}</div>
          </Card>
          <Card className="p-5 border-slate-200/70">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Ship to</div>
            <div className="text-sm text-slate-900">{order.address?.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{order.address?.phone}</div>
            <div className="text-xs text-slate-600 mt-1">{order.address?.line1}{order.address?.line2 ? `, ${order.address.line2}` : ""}, {order.address?.city}, {order.address?.state} {order.address?.pincode}</div>
          </Card>
          {order.delivery_slot?.label ? (
            <Card className="p-5 border-slate-200/70" data-testid="order-delivery-slot">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Delivery slot</div>
              <div className="text-sm text-slate-900">{order.delivery_slot.label}</div>
            </Card>
          ) : null}
          <Card className="p-5 border-slate-200/70">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Payment</div>
            <div className="text-sm capitalize">{order.payment_method === "whatsapp" ? "WhatsApp order" : order.payment_method}</div>
            <Badge className="mt-1 bg-slate-100 text-slate-700 border-0">{order.payment_status}</Badge>
            {order.whatsapp_url && (
              <a href={order.whatsapp_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-emerald-700 underline" data-testid="order-whatsapp-link">Open WhatsApp thread</a>
            )}
          </Card>
          {order.shiprocket && (
            <Card className="p-5 border-slate-200/70" data-testid="order-shiprocket">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Shiprocket</div>
              {order.shiprocket.error ? <div className="text-xs text-red-600">{order.shiprocket.error}</div> : (
                <div className="text-xs text-slate-600 space-y-1">
                  {order.shiprocket.shipment_id && <div>Shipment {order.shiprocket.shipment_id}</div>}
                  {order.shiprocket.awb && <div className="font-mono-nums">AWB {order.shiprocket.awb}</div>}
                  {order.shiprocket.status && <div>{order.shiprocket.status}</div>}
                </div>
              )}
              <Button variant="outline" size="sm" className="mt-3" data-testid="order-shiprocket-push" onClick={async () => { try { await api.post(`/merchant/orders/${id}/shiprocket`); toast.success("Sent to Shiprocket"); load(); } catch (e) { toast.error(extractErr(e)); } }}>Create / retry shipment</Button>
            </Card>
          )}
          {!order.shiprocket && (
            <Card className="p-5 border-slate-200/70" data-testid="order-shiprocket">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Shiprocket</div>
              <p className="text-xs text-slate-500">Push this order to Shiprocket (enable it in Settings → Shipping first).</p>
              <Button variant="outline" size="sm" className="mt-3" data-testid="order-shiprocket-push" onClick={async () => { try { await api.post(`/merchant/orders/${id}/shiprocket`); toast.success("Sent to Shiprocket"); load(); } catch (e) { toast.error(extractErr(e)); } }}>Create shipment</Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
